/**
 * Atomic Wheels Preorder UI
 * Handles:
 *   - Badge injection into visible product gallery
 *   - Add to Cart button text override
 *   - Mixed cart alert popup (with localStorage opt-out)
 *   - DYNAMIC variant change — re-renders badge/button/inputs/message when user picks another variant
 *
 * Reads config from <template>, <meta>, and <script data-aw-preorder-variants> rendered by Liquid.
 */
(function () {
  'use strict';

  // ============================================================
  //  VARIANTS DATA (rendered by Liquid for all variants of product)
  // ============================================================
  var VARIANTS_DATA = null;

  function loadVariantsData() {
    if (VARIANTS_DATA !== null) return VARIANTS_DATA;
    var el = document.querySelector('script[data-aw-preorder-variants]');
    if (!el) { VARIANTS_DATA = {}; return VARIANTS_DATA; }
    try {
      VARIANTS_DATA = JSON.parse(el.textContent || '{}');
    } catch (e) {
      console.warn('aw-preorder: cannot parse variants data', e);
      VARIANTS_DATA = {};
    }
    return VARIANTS_DATA;
  }

  function getSelectedVariantId() {
    // 1. URL ?variant=X
    var url = new URL(window.location.href);
    var v = url.searchParams.get('variant');
    if (v) return String(v);
    // 2. Form id field — must use [name="id"] without input/select prefix
    //    because Empire theme uses <select name="id"> (NOT <input>).
    //    Querying input[name="id"] would never match, falsely returning null.
    var idSelectors = [
      '[data-product-form] [name="id"]',
      'form[action*="/cart/add"] [name="id"]',
      'form[action$="/cart/add"] [name="id"]',
      'form.product-form [name="id"]',
      'form [name="id"]',
      '[name="id"]',
    ];
    for (var i = 0; i < idSelectors.length; i++) {
      var el = document.querySelector(idSelectors[i]);
      // For <select>, .value returns the currently selected option's value.
      // For <input>, .value returns its value. Both work the same way here.
      if (el && el.value && el.value !== 'not-selected') return String(el.value);
    }
    // 3. Empire-style: data-variant-id on a wrapper or input
    var dv = document.querySelector('[data-variant-id]:not([data-variant-id=""])');
    if (dv) {
      var dvId = dv.getAttribute('data-variant-id');
      if (dvId) return String(dvId);
    }
    // No fallback to "first variant in JSON" — that JSON contains ONLY preorder-
    // eligible variants, so falling back to it falsely surfaces preorder UI on
    // pages where the default-selected variant has stock (and is therefore NOT
    // in the JSON). Returning null lets applyForCurrentVariant() treat the page
    // as "no preorder" — safer default.
    return null;
  }

  // ============================================================
  //  BADGE INJECTION + UPDATE
  // ============================================================
  function buildBadgeHTML(cfg) {
    return '<span class="aw-preorder-badge aw-preorder-badge--' + cfg.badge_pos +
           ' aw-preorder-badge--' + cfg.badge_style +
           '" style="--aw-bg: ' + cfg.badge_bg + '; --aw-fg: ' + cfg.badge_fg + ';" data-aw-preorder-badge>' +
           '<span class="aw-preorder-badge__text">' + cfg.badge_text + '</span>' +
           '</span>';
  }

  function findGalleryTarget() {
    var galleries = document.querySelectorAll('.product-gallery');
    for (var i = 0; i < galleries.length; i++) {
      var r = galleries[i].getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return galleries[i];
    }
    return null;
  }

  function applyBadge(cfg) {
    // Remove old badge
    var old = document.querySelectorAll('[data-aw-preorder-badge]');
    old.forEach(function (n) { n.remove(); });

    if (!cfg) return;

    var target = findGalleryTarget();
    if (!target) return;

    var cs = window.getComputedStyle(target);
    if (cs.position === 'static') target.style.position = 'relative';

    target.insertAdjacentHTML('beforeend', buildBadgeHTML(cfg));
  }

  // ============================================================
  //  CTA BUTTON TEXT OVERRIDE
  // ============================================================
  function findCTAButton() {
    // Order: most specific first. Empire uses .product-form--atc-button + data-product-atc
    var selectors = [
      '.product-form--atc-button',
      '[data-product-atc]:not([data-aw-skip])',
      '[data-product-add-to-cart]',
      'button[name="add"]',
      'form[action*="/cart/add"] button[type="submit"]',
    ];
    for (var i = 0; i < selectors.length; i++) {
      var el = document.querySelector(selectors[i]);
      if (el) return el;
    }
    return null;
  }

  var DEFAULT_CTA_TEXT = null;  // remember theme's original text once
  var CURRENT_OVERRIDE = null;  // what we last set the button to
  var BUTTON_OBSERVER = null;   // MutationObserver — re-applies our text if theme overwrites

  function setButtonText(btn, text) {
    // For Empire, text is direct textContent (no child span)
    if ((btn.textContent || '').trim() !== text) {
      btn.textContent = text;
    }
  }

  function applyButtonText(cfg) {
    var btn = findCTAButton();
    if (!btn) return;

    if (cfg) {
      // OVERRIDE MODE — current variant is in preorder.
      // Force our preorder CTA text, enforce it via MutationObserver in case the
      // theme tries to reset it on variant change. Also colour the button per
      // metaobject config.
      if (DEFAULT_CTA_TEXT === null) {
        var currentTxt = (btn.textContent || '').trim();
        var data = loadVariantsData();
        var knownOverrides = Object.values(data).map(function (c) { return c.cta_text; });
        DEFAULT_CTA_TEXT = knownOverrides.indexOf(currentTxt) === -1 ? currentTxt : 'Add to cart';
      }
      CURRENT_OVERRIDE = cfg.cta_text;
      setButtonText(btn, CURRENT_OVERRIDE);
      btn.style.backgroundColor = cfg.cta_bg || '';

      if (!BUTTON_OBSERVER) {
        BUTTON_OBSERVER = new MutationObserver(function () {
          if (CURRENT_OVERRIDE && (btn.textContent || '').trim() !== CURRENT_OVERRIDE) {
            setButtonText(btn, CURRENT_OVERRIDE);
          }
        });
        BUTTON_OBSERVER.observe(btn, { childList: true, characterData: true, subtree: true });
      }
    } else {
      // RELEASE MODE — current variant is NOT preorder. Hands off the button:
      //   - Disconnect the observer so the theme can freely set "Add to cart" /
      //     "Sold out" / disabled state per its own variant-change logic.
      //   - Clear our overlaid colour.
      // We do NOT touch the button text here; the theme owns it in this mode.
      CURRENT_OVERRIDE = null;
      btn.style.backgroundColor = '';
      if (BUTTON_OBSERVER) {
        BUTTON_OBSERVER.disconnect();
        BUTTON_OBSERVER = null;
      }
    }
  }

  // ============================================================
  //  HIDDEN FORM INPUTS (selling_plan + line item property)
  // ============================================================
  function applyFormInputs(cfg) {
    var form = document.querySelector('[data-product-form], form[action*="/cart/add"]');
    if (!form) return;

    // Remove old hidden inputs
    form.querySelectorAll('[data-aw-preorder-selling-plan], [data-aw-preorder-line-property]').forEach(function (n) { n.remove(); });

    if (!cfg) return;

    if (cfg.selling_plan_id) {
      var spInput = document.createElement('input');
      spInput.type = 'hidden';
      spInput.name = 'selling_plan';
      spInput.value = cfg.selling_plan_id;
      spInput.setAttribute('data-aw-preorder-selling-plan', '');
      form.appendChild(spInput);
    }

    if (cfg.line_prop_key && cfg.line_prop_value) {
      var propInput = document.createElement('input');
      propInput.type = 'hidden';
      propInput.name = 'properties[' + cfg.line_prop_key + ']';
      propInput.value = cfg.line_prop_value;
      propInput.setAttribute('data-aw-preorder-line-property', '');
      form.appendChild(propInput);
    }
  }

  // ============================================================
  //  PAYMENT INFO BLOCK (Timesact-style "Partial payment: $10 per wheel")
  // ============================================================
  function applyPaymentInfo(cfg) {
    var existing = document.querySelector('[data-aw-preorder-payment-info]');
    var label = (cfg && cfg.payment_label) ? cfg.payment_label : '';

    if (!cfg || !label) {
      // No preorder OR full payment / no deposit — hide
      if (existing) existing.style.display = 'none';
      return;
    }

    if (existing) {
      // Update text + color in existing block
      var textEl = existing.querySelector('.aw-preorder-payment-info__text');
      if (textEl) textEl.textContent = label;
      if (cfg.badge_bg) existing.style.setProperty('--aw-bg', cfg.badge_bg);
      existing.style.display = '';
      return;
    }

    // No block on initial render — create one above the message block
    var msgEl = document.querySelector('[data-aw-preorder-message]');
    var anchor = msgEl
              || document.querySelector('[data-product-form]')
              || document.querySelector('form[action*="/cart/add"]');
    if (!anchor) return;

    var block = document.createElement('div');
    block.className = 'aw-preorder-payment-info';
    block.setAttribute('data-aw-preorder-payment-info', '');
    if (cfg.badge_bg) block.style.setProperty('--aw-bg', cfg.badge_bg);
    var icon = document.createElement('span');
    icon.className = 'aw-preorder-payment-info__icon';
    icon.setAttribute('aria-hidden', 'true');
    var text = document.createElement('span');
    text.className = 'aw-preorder-payment-info__text';
    text.textContent = label;
    block.appendChild(icon);
    block.appendChild(text);

    if (anchor.parentElement) {
      anchor.parentElement.insertBefore(block, anchor);
    }
  }

  // ============================================================
  //  PDP MESSAGE BLOCK
  // ============================================================
  function applyMessage(cfg) {
    var existing = document.querySelector('[data-aw-preorder-message]');

    if (!cfg) {
      // Variant has no preorder → hide message if exists
      if (existing) existing.style.display = 'none';
      return;
    }

    if (existing) {
      // Update existing message text + color
      existing.innerHTML = cfg.pdp_message_html || '';
      if (cfg.badge_bg) existing.style.setProperty('--aw-bg', cfg.badge_bg);
      existing.style.display = '';
      return;
    }

    // No message element on initial render — create one near the form
    var form = document.querySelector('[data-product-form]')
            || document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    var msg = document.createElement('div');
    msg.className = 'aw-preorder-message';
    msg.setAttribute('data-aw-preorder-message', '');
    if (cfg.badge_bg) msg.style.setProperty('--aw-bg', cfg.badge_bg);
    msg.innerHTML = cfg.pdp_message_html || '';

    // Insert after the form (or after its parent block)
    if (form.parentElement) {
      form.parentElement.insertBefore(msg, form.nextSibling);
    }
  }

  // ============================================================
  //  QUANTITY LIMIT (для stock preorders, не для BTO)
  // ============================================================
  var QTY_LIMIT_LISTENER_ATTACHED = false;
  var CURRENT_QTY_LIMIT = -1;  // -1 = unlimited

  function findQuantityInput() {
    return document.querySelector(
      'input[name="quantity"], input[type="number"][data-quantity-selector-input], .quantity-selector__input'
    );
  }

  function showQtyWarning(message) {
    // Remove old warning
    var existing = document.querySelector('[data-aw-qty-warning]');
    if (existing) existing.remove();

    if (!message) return;

    var warn = document.createElement('div');
    warn.setAttribute('data-aw-qty-warning', '');
    warn.className = 'aw-preorder-qty-warning';
    warn.textContent = message;
    var qInput = findQuantityInput();
    if (qInput && qInput.parentElement) {
      qInput.parentElement.parentElement.insertBefore(warn, qInput.parentElement.nextSibling);
    }
    // Auto-hide after 6 seconds (long enough to read; short enough to not pollute)
    setTimeout(function () { if (warn.parentElement) warn.remove(); }, 6000);
  }

  function applyQuantityLimit(cfg) {
    var qInput = findQuantityInput();
    if (!qInput) return;

    // We NEVER set max attribute and NEVER clamp the input value.
    // Customer is free to type/click any quantity (per business rule).
    // The only enforcement is at Add-to-cart time: if qty > preorder remaining,
    // we block the submit and show a warning. User must manually reduce qty.
    qInput.removeAttribute('max');

    if (!cfg || cfg.is_bto || cfg.qty_max < 0) {
      // No preorder limit (BTO is unlimited; regular product has no cap from us)
      CURRENT_QTY_LIMIT = -1;
      showQtyWarning(null);
      return;
    }

    CURRENT_QTY_LIMIT = parseInt(cfg.qty_max, 10);

    // Attach listeners once (they read CURRENT_QTY_LIMIT live, no need to re-attach on variant change)
    if (QTY_LIMIT_LISTENER_ATTACHED) return;
    QTY_LIMIT_LISTENER_ATTACHED = true;

    function buildWarning(limit) {
      return 'Only ' + limit + ' unit' + (limit === 1 ? '' : 's') +
        ' available for pre-order. Please reduce the quantity to add to cart.';
    }

    // Block form submit if qty exceeds preorder remaining. Does NOT mutate qty.
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.matches('[data-product-form], form[action*="/cart/add"]')) return;
      if (CURRENT_QTY_LIMIT < 0) return;
      var qInput = findQuantityInput();
      if (!qInput) return;
      var val = parseInt(qInput.value, 10) || 1;
      if (val > CURRENT_QTY_LIMIT) {
        e.preventDefault();
        e.stopPropagation();
        showQtyWarning(buildWarning(CURRENT_QTY_LIMIT));
      }
    }, true);

    // Same guard on Add-to-cart click (some themes don't fire form submit).
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('button[name="add"], [data-product-atc], .product-form--atc-button');
      if (!btn) return;
      if (CURRENT_QTY_LIMIT < 0) return;
      var qInput = findQuantityInput();
      if (!qInput) return;
      var val = parseInt(qInput.value, 10) || 1;
      if (val > CURRENT_QTY_LIMIT) {
        e.preventDefault();
        e.stopPropagation();
        showQtyWarning(buildWarning(CURRENT_QTY_LIMIT));
      }
    }, true);
  }

  // ============================================================
  //  ORCHESTRATE: apply config for currently selected variant
  // ============================================================
  function applyForCurrentVariant() {
    var data = loadVariantsData();
    var vid = getSelectedVariantId();
    var cfg = vid ? data[vid] : null;
    applyBadge(cfg);
    applyButtonText(cfg);
    applyFormInputs(cfg);
    applyMessage(cfg);
    applyPaymentInfo(cfg);
    applyQuantityLimit(cfg);
  }

  // ============================================================
  //  MIXED CART ALERT
  // ============================================================
  var DONT_SHOW_KEY = 'aw_preorder_mixed_alert_dismissed';

  function showMixedAlert() {
    if (localStorage.getItem(DONT_SHOW_KEY) === '1') return;
    var meta = document.querySelector('meta[name="aw-preorder-mixed-cart"]');
    if (!meta || meta.getAttribute('content') !== 'true') return;
    var template = document.querySelector('template[data-aw-preorder-mixed-alert-template]');
    if (!template) return;
    if (document.querySelector('[data-aw-preorder-modal]')) return;
    document.body.appendChild(template.content.cloneNode(true));
    document.querySelectorAll('[data-aw-preorder-modal-close]').forEach(function (el) {
      el.addEventListener('click', function () {
        var dontShow = document.querySelector('[data-aw-preorder-modal-dont-show]');
        if (dontShow && dontShow.checked) localStorage.setItem(DONT_SHOW_KEY, '1');
        var modal = document.querySelector('[data-aw-preorder-modal]');
        if (modal) modal.remove();
      });
    });
  }

  // ============================================================
  //  PERFORMANCE GUARD — early exit when this page has nothing to do
  // ============================================================
  // Skip ALL work on pages where:
  //   1. Variants data JSON is empty (no preorder-eligible variants), AND
  //   2. There's no mixed-cart alert template (= not the cart page either)
  //
  // Without this guard, every storefront page would run runAll() (which touches
  // the Add-to-Cart button and installs a MutationObserver that fights with
  // Empire's native variant-change handler — symptom: button text stays stuck
  // on the initial state when switching variants on non-preorder products).
  function hasWorkToDo() {
    if (Object.keys(loadVariantsData()).length > 0) return true;
    if (document.querySelector('template[data-aw-preorder-mixed-alert-template]')) return true;
    return false;
  }
  if (!hasWorkToDo()) {
    return;
  }

  // ============================================================
  //  RUN
  // ============================================================
  function runAll() {
    applyForCurrentVariant();
    showMixedAlert();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runAll);
  } else {
    runAll();
  }

  // Retry for async-rendered galleries (RIO, Variant Image Wizard, etc.)
  // Reduced from 10×500ms (5s) to 4×300ms (1.2s) — RIO typically appears <1s.
  var retries = 0;
  var retryInterval = setInterval(function () {
    retries++;
    if (findGalleryTarget()) {
      applyForCurrentVariant();
      clearInterval(retryInterval);
    } else if (retries >= 4) {
      clearInterval(retryInterval);
    }
  }, 300);

  // Re-apply on variant/cart change events
  ['variant:change', 'product:variant:change', 'cart:refresh', 'shopify:section:load'].forEach(function (evt) {
    document.addEventListener(evt, function () { setTimeout(applyForCurrentVariant, 100); });
  });

  // URL-change detection — Empire updates ?variant= via history.pushState on swatch
  // clicks but does NOT emit a native event. Replaces the previous 400ms polling.
  // Monkey-patch pushState/replaceState to emit a synthetic 'locationchange' event,
  // then react to it. Also handles browser back/forward via popstate.
  (function () {
    var origPush = history.pushState;
    var origReplace = history.replaceState;
    function emitLocationChange() {
      window.dispatchEvent(new Event('aw:locationchange'));
    }
    history.pushState = function () {
      var ret = origPush.apply(this, arguments);
      emitLocationChange();
      return ret;
    };
    history.replaceState = function () {
      var ret = origReplace.apply(this, arguments);
      emitLocationChange();
      return ret;
    };
    window.addEventListener('popstate', emitLocationChange);
  })();

  var lastVariantId = getSelectedVariantId();
  window.addEventListener('aw:locationchange', function () {
    var current = getSelectedVariantId();
    if (current !== lastVariantId) {
      lastVariantId = current;
      applyForCurrentVariant();
    }
  });

  // Also listen on variant selector input/select changes (covers themes that
  // don't update the URL but do fire native change events on form fields).
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t) return;
    var name = (t.name || '').toLowerCase();
    var id = (t.id || '').toLowerCase();
    if (name === 'id' || name.includes('option') || id.includes('variant') || (t.closest && t.closest('[data-product-form]'))) {
      setTimeout(applyForCurrentVariant, 150);
    }
  });
})();
